import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/sections/Navigation';
import Footer from '@/components/sections/Footer';
import { ArrowLeft } from 'lucide-react';
import { BRAND } from '@/lib/brand';

const H2 = ({ children }) => (
  <h2 className="font-serif text-[#0E2238] text-2xl md:text-3xl mt-12 mb-4 tracking-tight">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-[#2B2B2B]/80 text-base md:text-[1.05rem] leading-[1.75] font-light mb-4">{children}</p>
);

export default function Terms() {
  useEffect(() => {
    document.title = 'Terms of Use — Bowers Advisory Group';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F4EE]">
      <Navigation />
      <main className="pt-40 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/"
            data-testid="terms-back-link"
            className="inline-flex items-center gap-2 text-[#B68A35] hover:text-[#0E2238] text-sm tracking-wide mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <div className="overline text-[#B68A35] mb-6">Terms</div>
          <h1 className="font-serif text-[#0E2238] text-4xl md:text-5xl leading-[1.05] tracking-tight mb-4">
            Terms of Use
          </h1>
          <p className="text-[#2B2B2B]/55 text-sm tracking-wide">Effective date: July 2026 &middot; Last updated: July 2026</p>

          <div className="mt-10">
            <P>
              Welcome to {BRAND.website}. These Terms of Use ("Terms") govern your access to and use of
              the Bowers Advisory Group website (the "Site"). By using this Site, you agree to these
              Terms. If you do not agree, please do not use the Site.
            </P>

            <H2>1. Informational Purpose Only</H2>
            <P>
              The content on this Site is provided for general informational and educational purposes
              only. It does not constitute legal, tax, investment, or financial advice, and no
              attorney-client, fiduciary, or advisory relationship is created by your use of the Site.
              You should consult qualified professionals regarding your specific circumstances.
            </P>

            <H2>2. No Guarantees or Investment Advice</H2>
            <P>
              Bowers Advisory Group provides financial education, insurance-based strategies, and
              consulting services. We do not provide investment management or securities advice, and
              no guarantees are made regarding financial outcomes. Any references to strategies,
              products, or planning concepts are illustrative only.
            </P>

            <H2>3. Licensing &amp; State Availability</H2>
            <P>
              Insurance and consulting services offered through Bowers Advisory Group are available
              only in states where the firm and its representatives are properly licensed. Product
              availability and features may vary by license, carrier, and state requirements. Nothing
              on this Site constitutes an offer or solicitation in a jurisdiction where such offer or
              solicitation would be unlawful.
            </P>

            <H2>4. Use of the Site</H2>
            <P>
              You agree to use the Site only for lawful purposes and in a manner that does not
              infringe the rights of others or restrict their use of the Site. You may not use the
              Site to (a) transmit malicious code, (b) attempt to access data not intended for you,
              (c) interfere with the Site's operation, or (d) engage in fraudulent activity.
            </P>

            <H2>5. Intellectual Property</H2>
            <P>
              All content on this Site — including text, images, logos, marks, and the "Legacy
              Blueprint™" framework — is the property of Bowers Advisory Group or its licensors and is
              protected by applicable intellectual property laws. You may not reproduce, distribute,
              or create derivative works without prior written permission.
            </P>

            <H2>6. Third-Party Links &amp; Content</H2>
            <P>
              The Site may contain links to third-party websites or resources. We are not responsible
              for the content, accuracy, or practices of any third-party site, and inclusion of a link
              does not imply endorsement.
            </P>

            <H2>7. Disclaimer of Warranties</H2>
            <P>
              The Site and its content are provided "as is" and "as available," without warranties of
              any kind, express or implied. To the fullest extent permitted by law, we disclaim all
              warranties, including implied warranties of merchantability, fitness for a particular
              purpose, and non-infringement.
            </P>

            <H2>8. Limitation of Liability</H2>
            <P>
              To the fullest extent permitted by law, Bowers Advisory Group, its representatives, and
              its affiliates will not be liable for any indirect, incidental, consequential, special,
              or punitive damages arising out of or in connection with your use of the Site, even if
              advised of the possibility of such damages.
            </P>

            <H2>9. Privacy</H2>
            <P>
              Your use of the Site is also subject to our{' '}
              <Link to="/privacy" className="text-[#B68A35] hover:underline">Privacy Policy</Link>,
              which is incorporated into these Terms by reference.
            </P>

            <H2>10. Changes to These Terms</H2>
            <P>
              We may update these Terms from time to time. When we do, we will revise the "Last
              updated" date above. Your continued use of the Site after changes are posted constitutes
              your acceptance of the revised Terms.
            </P>

            <H2>11. Governing Law</H2>
            <P>
              These Terms are governed by the laws of the state in which Bowers Advisory Group
              maintains its principal place of business, without regard to conflict-of-law principles.
            </P>

            <H2>12. Contact</H2>
            <P>
              Questions about these Terms can be sent to{' '}
              <a className="text-[#B68A35] hover:underline" href={BRAND.emailHref}>{BRAND.email}</a>{' '}
              or by phone at{' '}
              <a className="text-[#B68A35] hover:underline" href={BRAND.phoneHref}>{BRAND.phone}</a>.
            </P>

            <div className="mt-16 p-6 border border-[#0E2238]/10 bg-white">
              <p className="text-[#2B2B2B]/70 text-xs leading-relaxed">
                These Terms of Use are provided for general informational purposes and do not
                constitute legal advice. Please consult a qualified attorney regarding your specific
                circumstances.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
